import React from 'react';
import { Wallet, Trash2, Clock, ArrowRight } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Боковая панель с сохраненными бюджетами
 */
const SavedBudgetsSidebar = ({ 
  savedBudgets, 
  deleteSavedBudget, 
  loadSavedBudget 
}) => {
  // Если нет сохраненных бюджетов
  if (!savedBudgets || savedBudgets.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Wallet className="w-5 h-5 mr-2 text-emerald-500" />
          Сохраненные бюджеты
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты бюджетов для сравнения.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Wallet className="w-5 h-5 mr-2 text-emerald-500" />
          Сохраненные бюджеты
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedBudgets.length} {getBudgetCountSuffix(savedBudgets.length)}
        </span>
      </div>

      <div className="space-y-4">
        {savedBudgets.map((budget) => (
          <div 
            key={budget.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {budget.name}
              </h4>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => loadSavedBudget(budget.id)}
                  className="text-gray-400 hover:text-emerald-500 dark:hover:text-emerald-400 p-1 rounded-full transition-colors"
                  title="Загрузить бюджет"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteSavedBudget(budget.id)}
                  className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded-full transition-colors"
                  title="Удалить бюджет"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-3">
              <Clock className="w-3.5 h-3.5 mr-1.5" />
              <span>
                {new Date(budget.date).toLocaleDateString('ru-RU', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </span>
            </div>

            {budget.results && (
              <div className="text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Доходы</div>
                    <div className="font-medium text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(budget.results.totalIncome)} ₽
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Расходы</div>
                    <div className="font-medium text-red-600 dark:text-red-400">
                      {formatCurrency(budget.results.totalExpenses)} ₽
                    </div>
                  </div>
                </div>
                
                <div className="mt-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Баланс</div>
                  <div className={`font-medium ${budget.results.balance >= 0 
                    ? 'text-emerald-600 dark:text-emerald-400' 
                    : 'text-red-600 dark:text-red-400'}`}
                  >
                    {formatCurrency(budget.results.balance)} ₽
                  </div>
                </div>
                
                {budget.results.status && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Оценка бюджета</div>
                    <div className="font-medium">
                      {budget.results.financialHealthScore} / 100
                      <span className={`ml-2 inline-block px-1.5 py-0.5 text-xs rounded ${
                        budget.results.status === 'excellent' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300' :
                        budget.results.status === 'good' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                        budget.results.status === 'fair' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {budget.results.status === 'excellent' ? 'Отлично' :
                         budget.results.status === 'good' ? 'Хорошо' :
                         budget.results.status === 'fair' ? 'Удовл.' : 'Внимание'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Сравнение с текущим вариантом будет добавлено здесь */}
          </div>
        ))}
      </div>

      {savedBudgets.length >= 2 && (
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => alert('Функция сравнения бюджетов будет добавлена в будущих обновлениях.')}
          >
            Сравнить бюджеты
          </Button>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слова "бюджет" в зависимости от числа
const getBudgetCountSuffix = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'бюджетов';
  }
  
  if (lastDigit === 1) {
    return 'бюджет';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'бюджета';
  }
  
  return 'бюджетов';
};

export default SavedBudgetsSidebar;